import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { useMemo } from "react";
import color from "../../Constants/colors";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ScheduledTask } from "../../Models/scheduledTask";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

interface Props {
  tasks: ScheduledTask[];
}

const parseLocal = (value: string): Date => new Date(value.replace(" ", "T"));

const formatRelative = (target: Date, now: Date, t: TFunction): string => {
  const diffMs = target.getTime() - now.getTime();
  const min = Math.round(diffMs / 60000);

  if (min < 1) return t("soon");
  if (min < 60) return t("inMin", { min });

  const hours = Math.round(min / 60);
  if (hours < 48) return t("inHours", { hours });

  const days = Math.round(hours / 24);
  return t("inDays", { days });
};

const formatShort = (d: Date): string =>
  d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/**
 * A lightweight horizontal timeline of upcoming scheduled runs. No charting
 * dependency — dots are positioned linearly between "now" and the furthest
 * future run. It reflects whatever set of tasks it's handed, so it honours the
 * list's active filters. Disabled tasks (which won't actually fire) are shown
 * muted so the picture stays honest.
 */
const ScheduledTasksTimeline = ({ tasks }: Props) => {
  const { t } = useTranslation("scheduledTasks");
  const { colors: workspace } = useWorkspaceColors();

  const { points, now, maxTime } = useMemo(() => {
    const nowDate = new Date();
    const upcoming = tasks
      .filter((t) => {
        if (!t.next_run_at) return false;
        return parseLocal(t.next_run_at).getTime() > nowDate.getTime();
      })
      .map((t) => ({ task: t, time: parseLocal(t.next_run_at as string) }))
      .sort((a, b) => a.time.getTime() - b.time.getTime());

    const max = upcoming.length
      ? upcoming[upcoming.length - 1].time.getTime()
      : nowDate.getTime();
    return { points: upcoming, now: nowDate, maxTime: max };
  }, [tasks]);

  if (points.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: color.gray400 }}>
        {t("noUpcomingRuns")}
      </Typography>
    );
  }

  const minTime = now.getTime();
  const denom = maxTime - minTime;
  const positionPct = (time: number): number => {
    if (denom <= 0) return 4;
    const pct = ((time - minTime) / denom) * 100;
    return Math.min(96, Math.max(4, pct));
  };

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ color: color.gray500, display: "block", mb: 1 }}
      >
        {t("upcomingRuns")} ({points.length})
      </Typography>

      <Box
        sx={{
          position: "relative",
          height: 132,
          mx: 1,
        }}
      >
        {/* axis line */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: 0,
            right: 0,
            height: "2px",
            bgcolor: color.gray300,
          }}
        />
        {/* "now" marker */}
        <Box
          sx={{
            position: "absolute",
            top: "calc(50% - 6px)",
            left: 0,
            width: 2,
            height: 12,
            bgcolor: color.gray400,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            top: "calc(50% + 10px)",
            left: 0,
            color: color.gray400,
            fontSize: "0.65rem",
          }}
        >
          {t("now")}
        </Typography>

        {points.map(({ task, time }, idx) => {
          const left = positionPct(time.getTime());
          const above = idx % 2 === 0;
          const isEnabled = task.enabled;
          const dotColor = isEnabled ? workspace.primary_dark : color.gray400;
          return (
            <Box key={task.id}>
              {/* stem */}
              <Box
                sx={{
                  position: "absolute",
                  left: `${left}%`,
                  top: above ? "calc(50% - 22px)" : "50%",
                  height: 22,
                  width: "1px",
                  bgcolor: color.gray300,
                }}
              />
              {/* dot */}
              <Tooltip
                arrow
                placement="top"
                title={`${task.name} · ${formatShort(time)}${
                  task.user_label ? ` · ${task.user_label}` : ""
                }${isEnabled ? "" : ` · ${t("disabled")}`}`}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: `${left}%`,
                    top: "calc(50% - 5px)",
                    transform: "translateX(-50%)",
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: dotColor,
                    border: `2px solid ${color.white}`,
                    boxShadow: `0 0 0 1px ${dotColor}`,
                    cursor: "default",
                  }}
                />
              </Tooltip>
              {/* label */}
              <Box
                sx={{
                  position: "absolute",
                  left: `${left}%`,
                  top: above ? "calc(50% - 52px)" : "calc(50% + 26px)",
                  transform: "translateX(-50%)",
                  width: 110,
                  textAlign: "center",
                  opacity: isEnabled ? 1 : 0.55,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontWeight: 600,
                    color: color.primary_dark,
                    fontSize: "0.68rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {task.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: color.gray500, fontSize: "0.62rem" }}
                >
                  {formatRelative(time, now, t)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Stack direction="row" justifyContent="flex-end">
        <Typography
          variant="caption"
          sx={{ color: color.gray400, fontSize: "0.65rem" }}
        >
          {formatShort(new Date(maxTime))}
        </Typography>
      </Stack>
    </Box>
  );
};

export default ScheduledTasksTimeline;
